import { all, call, fork, put, takeLatest, select } from "redux-saga/effects"
import { SagaIterator } from "redux-saga"
import { APIPostRequest } from "@/services/connection"
import { API_HEADERS, API_PMS_URL } from "@/constants/ApiSetting"
import * as actionCreation from "@/AppRedux/actions/admin/hotelList"
import * as actions from "@/AppRedux/actions/admin/hotelList/hotelListType"
import { RootState } from "@/AppRedux/reducers"

const normalizeHotelListItem = (hotel: any): actions.HotelListItem => ({
  id: String(hotel?.id ?? hotel?.hotel_id ?? ""),
  code: String(hotel?.code ?? hotel?.hotel_code ?? ""),
  name: String(hotel?.name ?? hotel?.hotel_name ?? ""),
  active: typeof hotel?.active === "boolean" ? hotel.active : true,
})

const normalizeHotelProfile = (hotel: any, fallbackId: string = ""): actions.HotelProfileData => ({
  id: String(hotel?.id ?? hotel?.hotel_id ?? fallbackId ?? ""),
  code: String(hotel?.code ?? hotel?.hotel_code ?? ""),
  name: String(hotel?.name ?? hotel?.hotel_name ?? ""),
  active: typeof hotel?.active === "boolean" ? hotel.active : true,
})

function* getHotelListProcess(): SagaIterator {
  try {
    const authState = yield select((state: RootState) => state.auth)
    const { propCode } = authState

    const url = `${API_PMS_URL}/admin/hotel/list`
    const API_BODY = {
      prop_code: propCode,
    }

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      const { result_data } = response.data || []
      const normalizedList = Array.isArray(result_data)
        ? result_data.map(normalizeHotelListItem)
        : []
      yield put(actionCreation.getHotelListSuccess(normalizedList))
    } else {
      const message = response?.data?.result_msg || "Failed to get hotel list"
      yield put(actionCreation.getHotelListFailed(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.getHotelListFailed(message))
  }
}

function* getHotelProfileProcess({ payload }: actions.GetHotelProfileRequest): SagaIterator {
  try {
    if (!payload?.id) {
      yield put(actionCreation.getHotelProfileFailed("Invalid hotel id"))
      return
    }

    const url = `${API_PMS_URL}/admin/hotel_profile/get`
    const API_BODY = {
      id: payload.id,
    }

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      const { result_data } = response.data || []
      const rawProfile = Array.isArray(result_data) ? result_data[0] : result_data

      if (!rawProfile) {
        yield put(actionCreation.getHotelProfileFailed("Hotel profile not found"))
        return
      }

      yield put(actionCreation.getHotelProfileSuccess(normalizeHotelProfile(rawProfile, payload.id)))
    } else {
      const message = response?.data?.result_msg || "Failed to get hotel profile"
      yield put(actionCreation.getHotelProfileFailed(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.getHotelProfileFailed(message))
  }
}

function* createHotelProfileProcess({ payload }: actions.CreateHotelProfileRequest): SagaIterator {
  try {
    const url = `${API_PMS_URL}/admin/hotel_profile/create`
    const API_BODY = {
      code: payload.code?.trim(),
      name: payload.name?.trim(),
      active: true,
      contents: [
        {
          lang: "en",
          title: `${payload.name} Hotel`,
          description: null,
          address: null,
          facilities: [],
        },
      ],
    }

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      yield put(actionCreation.createHotelProfileSuccess())
    } else {
      const message = response?.data?.result_msg || "Failed to create hotel profile"
      yield put(actionCreation.createHotelProfileFailed(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.createHotelProfileFailed(message))
  }
}

function* updateHotelProfileProcess({ payload }: actions.UpdateHotelProfileRequest): SagaIterator {
  try {
    if (!payload?.id) {
      yield put(actionCreation.updateHotelProfileFailed("Invalid hotel id"))
      return
    }

    const url = `${API_PMS_URL}/admin/hotel_profile/update`
    const API_BODY = {
      id: payload.id,
      code: payload.code?.trim(),
      name: payload.name?.trim(),
    }

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      yield put(actionCreation.updateHotelProfileSuccess())
    } else {
      const message = response?.data?.result_msg || "Failed to update hotel profile"
      yield put(actionCreation.updateHotelProfileFailed(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.updateHotelProfileFailed(message))
  }
}

function* updateHotelProfileEnabledProcess({ payload }: actions.UpdateHotelProfileEnabledRequest): SagaIterator {
  try {
    // Simulate an API call with a 50% chance of success
    const url = payload.active
      ? `${API_PMS_URL}/admin/hotel_profile/enable`
      : `${API_PMS_URL}/admin/hotel_profile/disable`
    const API_BODY = { id: payload.id }

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {

      yield put(actionCreation.updateHotelProfileEnabledSuccess())
    } else {
      const message = response?.data?.result_msg || "Failed to update active status"
      yield put(actionCreation.updateHotelProfileEnabledFailed(payload.id, message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.updateHotelProfileEnabledFailed(payload.id, message))
  }
}

function* watchGetHotelList() {
  yield takeLatest(actions.GET_HOTEL_LIST_REQUEST, getHotelListProcess)
}

function* watchGetHotelProfile() {
  yield takeLatest(actions.GET_HOTEL_PROFILE_REQUEST, getHotelProfileProcess)
}

function* watchCreateHotelProfile() {
  yield takeLatest(actions.CREATE_HOTEL_PROFILE_REQUEST, createHotelProfileProcess)
}

function* watchUpdateHotelProfile() {
  yield takeLatest(actions.UPDATE_HOTEL_PROFILE_REQUEST, updateHotelProfileProcess)
}

function* watchUpdateHotelProfileEnabled() {
  yield takeLatest(actions.UPDATE_HOTEL_PROFILE_ENABLED_REQUEST, updateHotelProfileEnabledProcess)
}

export default function* hotelListSaga() {
  yield all([
    fork(watchGetHotelList),
    fork(watchGetHotelProfile),
    fork(watchCreateHotelProfile),
    fork(watchUpdateHotelProfile),
    fork(watchUpdateHotelProfileEnabled),
  ])
}
